import {
  filestackUploadSignatureSponsorBooths,
  uploadSignatureFilestack,
  type AssetKindEnum,
  type IntentEnum
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import * as filestack from 'filestack-js';

import { FILESTACK_API_KEY } from '@/utils/constants';

export interface FilestackSecurity {
  policy: string;
  signature: string;
}

type FilestackClient = ReturnType<typeof filestack.init>;
type FilestackUploadOptions = Parameters<FilestackClient['upload']>[1];
type FilestackStoreOptions = Parameters<FilestackClient['upload']>[2];

interface UploadFileWithFilestackSecurityRetryArgs {
  file: File;
  uploadOptions: FilestackUploadOptions;
  storeOptions: FilestackStoreOptions;
  filestackIntent: FilestackIntentArgs;
  isSignedFilestackUploadsEnabled: boolean;
}

export type FilestackIntentArgs =
  | { intent: 'sponsor_booth_asset'; assetKind: AssetKindEnum; event: string; boothId: string }
  | { intent: Extract<IntentEnum, 'organization_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'event_asset'>; assetKind: string; event: string }
  | { intent: Extract<IntentEnum, 'org_filler_slide_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'public_image_event_asset'>; assetKind: string; event: string }
  | { intent: Extract<IntentEnum, 'public_image_org_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'public_image_series_asset'>; assetKind: string; seriesId: string }
  | { intent: Extract<IntentEnum, 'studio_template_asset'>; assetKind: string; templateId: string }
  | { intent: Extract<IntentEnum, 'series_registration_asset'>; assetKind: string; seriesId: string }
  | { intent: Extract<IntentEnum, 'bucket_root_asset'>; assetKind: string };

function buildBody(args: FilestackIntentArgs): Record<string, string> {
  const body: Record<string, string> = { intent: args.intent, asset_kind: args.assetKind };
  if (args.intent === 'event_asset' || args.intent === 'public_image_event_asset') {
    body.event = args.event;
  } else if (args.intent === 'public_image_series_asset' || args.intent === 'series_registration_asset') {
    body.series_id = args.seriesId;
  } else if (args.intent === 'studio_template_asset') {
    body.template_id = args.templateId;
  }
  return body;
}

function buildCacheKey(args: FilestackIntentArgs): string {
  const event = 'event' in args ? args.event : 'none';
  const series = 'seriesId' in args ? args.seriesId : 'none';
  const template = 'templateId' in args ? args.templateId : 'none';
  const booth = 'boothId' in args ? args.boothId : 'none';
  return `${args.intent}:${event}:${series}:${template}:${booth}:${args.assetKind}`;
}

const EARLY_EXPIRY_BUFFER_MS = 60 * 1000;
const FILESTACK_REQUEST_ERROR_TYPE = 'request';
const FILESTACK_AUTH_STATUS_CODES = new Set([401, 403]);
const FILESTACK_SECURITY_ERROR_TOKENS = ['policy', 'signature', 'security', 'expired', 'unauthorized', 'forbidden'];

const securityCache = new Map<string, { security: FilestackSecurity; expiresAtMs: number }>();

export function invalidateFilestackSecurity(args: FilestackIntentArgs): void {
  securityCache.delete(buildCacheKey(args));
}

function getFilestackRequestDetails(error: Record<string, unknown>): Record<string, unknown> | null {
  if (error.type !== FILESTACK_REQUEST_ERROR_TYPE || !error.details || typeof error.details !== 'object') {
    return null;
  }
  return error.details as Record<string, unknown>;
}

function stringifyErrorDetail(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return '';
}

export function isFilestackSecurityError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const maybeError = error as Record<string, unknown>;
  const requestDetails = getFilestackRequestDetails(maybeError);
  if (!requestDetails) {
    return false;
  }

  if (typeof requestDetails.code === 'number' && FILESTACK_AUTH_STATUS_CODES.has(requestDetails.code)) {
    return true;
  }

  const searchableText = [maybeError.message, requestDetails.data].map(stringifyErrorDetail).join(' ').toLowerCase();

  return FILESTACK_SECURITY_ERROR_TOKENS.some(token => searchableText.includes(token));
}

export async function getFilestackSecurity(
  args: FilestackIntentArgs,
  options: { forceRefresh?: boolean } = {}
): Promise<{ security: FilestackSecurity }> {
  const cacheKey = buildCacheKey(args);
  const cached = securityCache.get(cacheKey);
  if (!options.forceRefresh && cached && Date.now() < cached.expiresAtMs - EARLY_EXPIRY_BUFFER_MS) {
    return { security: cached.security };
  }

  const response =
    args.intent === 'sponsor_booth_asset'
      ? await filestackUploadSignatureSponsorBooths({
          id: args.boothId,
          body: { event: args.event, asset_kind: args.assetKind }
        })
      : ((await uploadSignatureFilestack({ body: buildBody(args) as never })) as {
          policy: string;
          signature: string;
          expiresAt: number;
        });
  const security = { policy: response.policy, signature: response.signature };
  securityCache.set(cacheKey, { security, expiresAtMs: response.expiresAt * 1000 });
  return { security };
}

export async function resolveFilestackSecurity(
  args: FilestackIntentArgs,
  isSignedFilestackUploadsEnabled: boolean
): Promise<FilestackSecurity | null> {
  if (!isSignedFilestackUploadsEnabled) {
    return null;
  }
  try {
    const { security } = await getFilestackSecurity(args);
    return security;
  } catch (e) {
    logger.warn('Failed to fetch Filestack security, upload will fail', e);
    return null;
  }
}

export async function uploadWithFilestackSecurityRetry<T>(
  args: FilestackIntentArgs,
  isSignedFilestackUploadsEnabled: boolean,
  upload: (security?: FilestackSecurity) => Promise<T>
): Promise<T> {
  if (!isSignedFilestackUploadsEnabled) {
    return upload(undefined);
  }

  const security = await resolveFilestackSecurity(args, true);
  try {
    return await upload(security || undefined);
  } catch (error) {
    if (!security || !isFilestackSecurityError(error)) {
      throw error;
    }
    invalidateFilestackSecurity(args);
    const { security: refreshedSecurity } = await getFilestackSecurity(args, { forceRefresh: true });
    return upload(refreshedSecurity);
  }
}

export async function uploadFileWithFilestackSecurityRetry({
  file,
  uploadOptions,
  storeOptions,
  filestackIntent,
  isSignedFilestackUploadsEnabled
}: UploadFileWithFilestackSecurityRetryArgs): ReturnType<FilestackClient['upload']> {
  return uploadWithFilestackSecurityRetry(filestackIntent, isSignedFilestackUploadsEnabled, security => {
    const client = filestack.init(FILESTACK_API_KEY, security ? { security } : undefined);
    return client.upload(file, uploadOptions, storeOptions, undefined, security);
  });
}
